import { Link } from "react-router-dom";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import useTransactions from "../../../../hooks/useTransactions";
import localeText from "../../../../locale";
import { formatDate } from "../../../../utilities/formatDate";
import { formatFloatToInteger } from "../../../../utilities/formatFloatToInteger";
import { formatPrice } from "../../../../utilities/formatPrice";
import "../../pages/FinancialHub/components/FinancialTransactionsTable/FinancialTransactionsTable.css";

const breadcrumbMap = {
  extracoinStatement: {
    parent: { to: "/dashboard-financeiro/extracredit", label: "extracredit" },
    current: "extracoinStatement",
  },
  walletStatement: {
    parent: { to: "/dashboard-financeiro/carteira", label: "wallet" },
    current: "walletStatement",
  },
  extrabonusStatement: {
    parent: { to: "/dashboard-financeiro", label: "financial" },
    current: "extrabonusStatement",
  },
  bonusStatement: {
    parent: { to: "/dashboard-financeiro", label: "financial" },
    current: "bonusStatement",
  },
};

const StatementTable = (props) => {
  const statementType = props.statementType;
  const {
    getTransactionTable,
    loading,
    statementData,
    routeName,
    dateBegin,
    setDateBegin,
    dateEnd,
    setDateEnd,
  } = useTransactions(statementType ? { statementType } : {});

  const breadcrumb = breadcrumbMap[routeName];
  const showDateFilter = routeName !== "extracoinStatement";

  return (
    <div>
      {breadcrumb && (
        <nav
          className="flex align-items-center gap-2 mb-3 text-sm text-600"
          aria-label="Breadcrumb"
        >
          <Link to="/dashboard-financeiro" style={{ color: "var(--theme-text-muted, #344563)" }}>
            {localeText("financial")}
          </Link>
          <span>/</span>
          {breadcrumb.parent && (
            <>
              <Link to={breadcrumb.parent.to} style={{ color: "var(--theme-text-muted, #344563)" }}>
                {localeText(breadcrumb.parent.label)}
              </Link>
              <span>/</span>
            </>
          )}
          <span style={{ color: "var(--theme-text, #344563)" }}>
            {localeText(breadcrumb.current || routeName)}
          </span>
        </nav>
      )}

      <div className="overflow-hidden">
        {props.plan !== "Manager" &&
        props.plan !== "Partner" &&
        props.plan !== "Seller" &&
        props.bonus &&
        routeName !== "extrabonusStatement" ? (
          <div className="text-center">
            <i className="pi pi-lock text-8xl text-400 my-4" />
            <h2 className="my-6">{localeText("planNotAllowed")}</h2>
          </div>
        ) : (
          <>
            <div className="mb-3">
              <div className="my-0 font-semibold" style={{ color: "var(--theme-text, #172B4D)" }}>
                {localeText(routeName)}
              </div>
              <div
                className="text-sm mt-2"
                style={{ color: "var(--theme-text-muted, #5C5C5C)" }}
              >
                {localeText("statementSubtitle")}
              </div>
            </div>

            {showDateFilter && (
              <div className="p-4 border-bottom-1 border-200">
                <DateFilterSection
                  dateBegin={dateBegin}
                  setDateBegin={setDateBegin}
                  dateEnd={dateEnd}
                  setDateEnd={setDateEnd}
                  getTransactionTable={getTransactionTable}
                  loading={loading}
                />
              </div>
            )}

            <div className="financial-transactions-table">
              <DataTable
                value={statementData}
                paginator
                responsiveLayout="scroll"
                loading={loading}
                paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                currentPageReportTemplate={localeText("showingFirstToLast")}
                rows={10}
                rowsPerPageOptions={[10, 20, 50]}
                className="financial-transactions-datatable"
                emptyMessage={
                  <div className="flex flex-column align-items-center justify-content-center py-8 gap-3">
                    <div
                      className="border-round flex align-items-center justify-content-center"
                      style={{
                        width: "120px",
                        height: "120px",
                        backgroundColor: "color-mix(in srgb, var(--theme-border) 35%, transparent)",
                      }}
                    >
                      <i className="pi pi-inbox text-6xl text-400" />
                    </div>
                    <p className="m-0" style={{ color: "var(--theme-text-muted, #5C5C5C)" }}>
                      {localeText("noOperationsRecords")}
                    </p>
                  </div>
                }
              >
                <Column
                  header={localeText("date")}
                  body={(rowData) => (rowData.date ? <div>{formatDate(rowData.date)}</div> : null)}
                />
                <Column field="description" header={localeText("description")} />
                <Column
                  header={localeText("inbound")}
                  body={(rowData) => {
                    const inboundValue = props.bonus ? rowData.income : rowData.income;
                    return props.onlyIntegerValues
                      ? formatFloatToInteger(inboundValue)
                      : `${props.currency} ${formatPrice(inboundValue)}`;
                  }}
                />
                <Column
                  header={localeText("outbound")}
                  body={(rowData) => {
                    const outboundValue = props.bonus ? rowData.outcome : rowData.outcome;
                    return props.onlyIntegerValues
                      ? formatFloatToInteger(outboundValue)
                      : `${props.currency} ${formatPrice(outboundValue)}`;
                  }}
                />
                <Column
                  header={localeText("status")}
                  body={(rowData) => {
                    const rowContent = {
                      COMPLETED: { text: "completed", color: "#46a748" },
                      PENDING: { text: "pending", color: "#F7B005" },
                      PENDINGVOUCHER: { text: "pendingVoucher", color: "#F7B005" },
                      CANCELED: { text: "canceled", color: "#eb4034" },
                    };
                    const rowContentObj = rowContent[rowData.status] ?? null;
                    return rowContentObj ? (
                      <span
                        className="team-member-role text-0 p-1 border-round"
                        style={{ backgroundColor: rowContentObj.color }}
                      >
                        {localeText(rowContentObj.text)}
                      </span>
                    ) : null;
                  }}
                />
              </DataTable>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const DateFilterSection = ({
  dateBegin,
  setDateBegin,
  dateEnd,
  setDateEnd,
  getTransactionTable,
  loading,
}) => {
  return (
    <div className="lg:flex md:flex lg:align-items-center md:align-items-center lg:flex-wrap md:flex-wrap gap-3">
      <div>
        <label htmlFor="dateBegin">{localeText("from")}</label>
        <Calendar
          id="dateBegin"
          className="mt-2 mr-2 w-full lg:w-auto md:w-auto"
          value={dateBegin}
          onChange={(e) => setDateBegin(e.value)}
          dateFormat="dd/mm/yy"
        />
      </div>
      <div>
        <label htmlFor="dateEnd">{localeText("toFrom")}</label>
        <Calendar
          id="dateEnd"
          className="mt-2 w-full lg:w-auto md:w-auto"
          dateFormat="dd/mm/yy"
          value={dateEnd}
          onChange={(e) => setDateEnd(e.value)}
        />
      </div>
      <Button
        label={localeText("check")}
        onClick={() => getTransactionTable(dateBegin, dateEnd)}
        loading={loading}
      />
    </div>
  );
};

export default StatementTable;
